/**
 * AddStudy Page — link-or-create against the platform study registry
 * (source-of-truth plan §5.3, P3).
 *
 * Studies are created once, in eReg; Site Organizer lists every registry study
 * at the site automatically. "Adding" a study is therefore: find it in the
 * registry (it may already exist) and jump to it — or follow the
 * "Create new study in eReg" deep link, which returns here with
 * ?created_protocol_id so we can land on the fresh study's detail page.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import api from '../../services/api';
import { fieldCls, StudyPicker } from '../../ui';
import type { RegistryStudy } from '../../ui';

interface Site {
  id: string;
  name: string;
  code: string;
}

const RETURN_TO = '/siteorganizer/add';

export function AddStudy() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState('');

  // Return leg from "Create new study in eReg": land on the new study.
  useEffect(() => {
    const createdId = searchParams.get('created_protocol_id');
    if (createdId) {
      setSearchParams({}, { replace: true });
      navigate(`/siteorganizer/study/${createdId}`, { replace: true });
    }
  }, [searchParams, setSearchParams, navigate]);

  // Fetch sites for the scope dropdown (multi-site users)
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get('/api/sites');
        setSites(response.data);
        if (response.data.length === 1) {
          setSiteId(response.data[0].id);
        }
      } catch (err) {
        console.error('Error fetching sites:', err);
      }
    };
    fetchSites();
  }, []);

  const handleSelect = (study: RegistryStudy) => {
    navigate(`/siteorganizer/study/${study.id}`);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/siteorganizer/studies')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
          >
            <ArrowLeft size={16} />
            Back to Studies
          </button>
          <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">Add Study</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Find the study in the platform registry, or create it in eReg — it appears here automatically
          </p>
        </div>
      </div>

      {/* Picker */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            {sites.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Site
                </label>
                <select
                  value={siteId}
                  onChange={e => setSiteId(e.target.value)}
                  className={`w-full ${fieldCls}`}
                >
                  <option value="">All my sites</option>
                  {sites.map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name} ({site.code})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Study
              </label>
              <StudyPicker
                siteId={siteId || undefined}
                onSelect={handleSelect}
                createReturnTo={RETURN_TO}
                createReturnLabel="Site Organizer"
                autoFocus
              />
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-primary-50 p-3 text-sm text-primary-700 dark:bg-primary-900/20 dark:text-primary-400">
              <FileText className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>
                Studies are created and managed in eReg — the platform's study registry. Selecting a
                study takes you straight to its Organizer page (team, bookmarks, contacts).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddStudy;
